/**
 * Patient Contacts API - client-side API surface for relational contact storage in MySQL.
 *
 * Interacts with keyon/tswebhook.php (PatientContacts API_Meth dispatch)
 * backed by the `patient_contacts` table in MySQL.
 *
 * Domain context:
 * - Contacts belong to a patient within an organization (accountnumber, patient_unique_id).
 * - Contacts are relational records (family members, emergency contacts, caregivers, proxies)
 *   and not portal accounts until registered.
 * - Supports prioritizing / favoriting contacts (primary_contact flag).
 */

import axios from "axios";
import { CURR_API } from "@/constants/apis";
import store from "@/store";

const WEBHOOK = `${CURR_API}/tswebhook.php`;

/**
 * Resolves accountnumber and patient_unique_id from explicit params or falls back
 * to the currently logged in / impersonated patient in Vuex store.
 *
 * @param {Object} [params]
 * @returns {{ accountnumber: string, patientUniqueId: string }}
 */
export function resolvePatientScope(params = {}) {
  const allData = store?.getters ? store.getters["login/getAllData"] : {};
  const patient = allData?.patient || {};

  const accountnumber =
    params.accountnumber ||
    params.accountNumber ||
    patient.externId2 ||
    patient.externid2 ||
    allData?.user?.accountnumber ||
    "";

  const patientUniqueId =
    params.patient_unique_id ||
    params.patientUniqueId ||
    params.patientId ||
    patient.externId ||
    patient.externid ||
    patient.psersonalID ||
    patient.id ||
    "";

  return {
    accountnumber: String(accountnumber || "").trim(),
    patientUniqueId: String(patientUniqueId || "").trim(),
  };
}

/**
 * Fetch all contacts for a patient.
 *
 * @param {Object} [params] Optional scope overrides ({ accountnumber, patient_unique_id })
 * @returns {Promise<Array>} List of contact records
 */
export async function getPatientContacts(params = {}) {
  const { accountnumber, patientUniqueId } = resolvePatientScope(params);
  if (!accountnumber || !patientUniqueId) {
    console.warn("getPatientContacts: Missing patient scope", { accountnumber, patientUniqueId });
    return [];
  }

  try {
    const res = await axios.post(
      WEBHOOK,
      {
        PatientContacts: {
          API_Meth: "GetPatientContacts",
          accountnumber,
          patient_unique_id: patientUniqueId,
        },
      },
      { headers: { "Content-Type": "application/json;" } }
    );

    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    return [];
  } catch (err) {
    console.error("Failed to load patient contacts:", err);
    return [];
  }
}

/**
 * Save (create or update) a patient contact.
 *
 * @param {Object} contactForm Contact data payload
 * @returns {Promise<Array|Object>} Resolves to [] on success for legacy compatibility, or error array
 */
export async function savePatientContact(contactForm = {}) {
  const { accountnumber, patientUniqueId } = resolvePatientScope(contactForm);
  if (!accountnumber || !patientUniqueId) {
    return ["Missing patient scope (account number or patient ID)"];
  }

  const payload = {
    ...contactForm,
    accountnumber,
    patient_unique_id: patientUniqueId,
    contact_uuid: contactForm.contact_uuid || contactForm.id || "",
    primary_contact: contactForm.primaryContact || contactForm.primary_contact ? 1 : 0,
  };

  try {
    const res = await axios.post(
      WEBHOOK,
      {
        PatientContacts: {
          API_Meth: "SavePatientContact",
          ...payload,
        },
      },
      { headers: { "Content-Type": "application/json;" } }
    );

    if (res?.data?.status === "Successful") {
      // Return empty array on success to match legacy caller expectations in UserContact.vue
      return [];
    }

    const errorMsg = res?.data?.message || res?.data?.error || "Failed to save contact.";
    return [errorMsg];
  } catch (err) {
    console.error("Failed to save patient contact:", err);
    return [err?.message || "Network error while saving contact."];
  }
}

/**
 * Delete a patient contact.
 *
 * @param {Object} contactForm Must contain id or contact_uuid
 * @returns {Promise<Array>} Resolves to [] on success, or error array
 */
export async function deletePatientContact(contactForm = {}) {
  const { accountnumber, patientUniqueId } = resolvePatientScope(contactForm);
  const contactUuid = contactForm.contact_uuid || contactForm.id || "";

  if (!contactUuid) {
    return ["Missing contact identifier"];
  }

  try {
    const res = await axios.post(
      WEBHOOK,
      {
        PatientContacts: {
          API_Meth: "DeletePatientContact",
          accountnumber,
          patient_unique_id: patientUniqueId,
          contact_uuid: contactUuid,
        },
      },
      { headers: { "Content-Type": "application/json;" } }
    );

    if (res?.data?.status === "Successful") {
      return [];
    }

    const errorMsg = res?.data?.message || res?.data?.error || "Failed to delete contact.";
    return [errorMsg];
  } catch (err) {
    console.error("Failed to delete patient contact:", err);
    return [err?.message || "Network error while deleting contact."];
  }
}

/**
 * Set or clear primary (favorited / prioritized) status for a patient contact.
 * Setting a contact as primary automatically demotes other contacts for the same patient.
 *
 * @param {Object} contactForm Must contain id or contact_uuid and primaryContact boolean
 * @returns {Promise<Array>} Resolves to [] on success, or error array
 */
export async function setPrimaryPatientContact(contactForm = {}) {
  const { accountnumber, patientUniqueId } = resolvePatientScope(contactForm);
  const contactUuid = contactForm.contact_uuid || contactForm.id || "";
  const isPrimary = Boolean(
    contactForm.primaryContact !== undefined
      ? contactForm.primaryContact
      : contactForm.primary_contact
  );

  if (!contactUuid) {
    return ["Missing contact identifier"];
  }

  try {
    const res = await axios.post(
      WEBHOOK,
      {
        PatientContacts: {
          API_Meth: "SetPrimaryContact",
          accountnumber,
          patient_unique_id: patientUniqueId,
          contact_uuid: contactUuid,
          primary_contact: isPrimary ? 1 : 0,
        },
      },
      { headers: { "Content-Type": "application/json;" } }
    );

    if (res?.data?.status === "Successful") {
      return [];
    }

    const errorMsg = res?.data?.message || res?.data?.error || "Failed to set contact priority.";
    return [errorMsg];
  } catch (err) {
    console.error("Failed to set contact priority:", err);
    return [err?.message || "Network error while updating contact priority."];
  }
}
